// URL-synced filter state for the Activity feed (and any page that wants
// shareable filters). Modeled on the legacy useURLFilters, but views and
// drill-ins live in the path in v2 — only filters belong in the query string.

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const PARAM = {
  APPS: 'apps',
  SERVICES: 'services',
  ENVS: 'envs',
  TYPES: 'types',
  USERS: 'users',
  TIME: 'time',
  FROM: 'from',
  TO: 'to',
  DEP_ID: 'dep_id',
} as const;

export const VALID_TIME_PRESETS = ['all', '1h', '24h', '7d', '30d', 'custom'] as const;
export type TimePreset = typeof VALID_TIME_PRESETS[number];

const parseCsv = (value: string | null): string[] => (value ? value.split(',').filter(Boolean) : []);
const toCsv = (arr: string[]): string | null => (arr.length > 0 ? arr.join(',') : null);

export interface V2Filters {
  apps: string[];
  services: string[];
  envs: string[];
  types: string[];
  users: string[];
  time: TimePreset;
  /** Custom range bounds (ISO strings) — only meaningful when time === 'custom'. */
  customFrom: string | null;
  customTo: string | null;
  depId: string;
  setApps: (v: string[]) => void;
  setServices: (v: string[]) => void;
  setEnvs: (v: string[]) => void;
  setTypes: (v: string[]) => void;
  setUsers: (v: string[]) => void;
  setTime: (v: TimePreset) => void;
  setCustomFrom: (v: string | null) => void;
  setCustomTo: (v: string | null) => void;
  setDepId: (v: string) => void;
  clearAll: () => void;
  hasActiveFilters: boolean;
  applyFilterSet: (filters: {
    apps?: string[];
    services?: string[];
    envs?: string[];
    types?: string[];
    users?: string[];
  }) => void;
}

export function useV2Filters(): V2Filters {
  const [searchParams, setSearchParams] = useSearchParams();

  const apps = useMemo(() => parseCsv(searchParams.get(PARAM.APPS)), [searchParams]);
  const services = useMemo(() => parseCsv(searchParams.get(PARAM.SERVICES)), [searchParams]);
  const envs = useMemo(() => parseCsv(searchParams.get(PARAM.ENVS)), [searchParams]);
  const types = useMemo(() => parseCsv(searchParams.get(PARAM.TYPES)), [searchParams]);
  const users = useMemo(() => parseCsv(searchParams.get(PARAM.USERS)), [searchParams]);
  const depId = searchParams.get(PARAM.DEP_ID) || '';

  const time: TimePreset = useMemo(() => {
    const val = searchParams.get(PARAM.TIME);
    return val && (VALID_TIME_PRESETS as readonly string[]).includes(val)
      ? (val as TimePreset)
      : 'all';
  }, [searchParams]);

  const customFrom = searchParams.get(PARAM.FROM);
  const customTo = searchParams.get(PARAM.TO);

  const setCsvParam = useCallback(
    (key: string) => (value: string[]) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        const csv = toCsv(value);
        if (csv) next.set(key, csv);
        else next.delete(key);
        return next;
      });
    },
    [setSearchParams]
  );

  const setApps = useMemo(() => setCsvParam(PARAM.APPS), [setCsvParam]);
  const setServices = useMemo(() => setCsvParam(PARAM.SERVICES), [setCsvParam]);
  const setEnvs = useMemo(() => setCsvParam(PARAM.ENVS), [setCsvParam]);
  const setTypes = useMemo(() => setCsvParam(PARAM.TYPES), [setCsvParam]);
  const setUsers = useMemo(() => setCsvParam(PARAM.USERS), [setCsvParam]);

  const setTime = useCallback(
    (value: TimePreset) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (value && value !== 'all') next.set(PARAM.TIME, value);
        else next.delete(PARAM.TIME);
        // Custom bounds only make sense with the custom preset
        if (value !== 'custom') {
          next.delete(PARAM.FROM);
          next.delete(PARAM.TO);
        }
        return next;
      });
    },
    [setSearchParams]
  );

  const setIsoParam = useCallback(
    (key: string) => (value: string | null) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setCustomFrom = useMemo(() => setIsoParam(PARAM.FROM), [setIsoParam]);
  const setCustomTo = useMemo(() => setIsoParam(PARAM.TO), [setIsoParam]);

  const setDepId = useCallback(
    (value: string) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (value) next.set(PARAM.DEP_ID, value);
          else next.delete(PARAM.DEP_ID);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const clearAll = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.values(PARAM).forEach(key => next.delete(key));
      return next;
    });
  }, [setSearchParams]);

  const applyFilterSet = useCallback(
    (filters: {
      apps?: string[];
      services?: string[];
      envs?: string[];
      types?: string[];
      users?: string[];
    }) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        const apply = (key: string, value?: string[]) => {
          const csv = toCsv(value || []);
          if (csv) next.set(key, csv);
          else next.delete(key);
        };
        apply(PARAM.APPS, filters.apps);
        apply(PARAM.SERVICES, filters.services);
        apply(PARAM.ENVS, filters.envs);
        apply(PARAM.TYPES, filters.types);
        apply(PARAM.USERS, filters.users);
        return next;
      });
    },
    [setSearchParams]
  );

  const hasActiveFilters =
    apps.length > 0 ||
    services.length > 0 ||
    envs.length > 0 ||
    types.length > 0 ||
    users.length > 0 ||
    !!depId ||
    time !== 'all';

  return {
    apps,
    services,
    envs,
    types,
    users,
    time,
    customFrom,
    customTo,
    depId,
    setApps,
    setServices,
    setEnvs,
    setTypes,
    setUsers,
    setTime,
    setCustomFrom,
    setCustomTo,
    setDepId,
    clearAll,
    hasActiveFilters,
    applyFilterSet,
  };
}
